import { defineModule, definePermission } from '@shared/contract'

// Einkauf: vom Bestellvorschlag bis zur geprüften Eingangsrechnung.
//
// Rechte – alle je Standort vergebbar (db/seeds/demo/roles.ts, Abschnitt „Einkauf“):
//   * Vorschlag lesen heißt: sehen, was am eigenen Standort fehlt und was dazu schon bestellt ist. Einkaufspreise
//     und Bestellwerte zusätzlich nur mit `costs.read`.
//   * Filialleitungen fordern an (`requisition.create`) – bestellen kann nur, wer `order.write` hält.
//   * `order.send` schickt bestellte Bestellungen per E-Mail an den Lieferanten (docs/anmeldung/SPEZIFIKATION.md §5.3).
//   * `order.approve` trägt die Grenze `maxOrderValueCents`: Bis dahin bestellt die Person selbst; darüber wartet
//     die Bestellung auf die Freigabe einer ZWEITEN Person, deren Grenze den Wert deckt.
//   * `invoice.check` erfasst, prüft und gibt Eingangsrechnungen frei. Mit der Freigabe entsteht der offene Posten;
//     Zahlung und Export gehören dem Modul Finanzen.
const GROUP = 'Einkauf'

export default defineModule({
  id: 'purchasing',
  title: 'Einkauf',
  description: 'Bestellvorschläge, Anforderungen der Filialen, Bestellungen mit Freigabe, Wareneingang zur Bestellung, Rechnungsprüfung.',
  dependsOn: ['platform', 'masterdata', 'inventory'],
  permissions: [
    definePermission({
      key: 'purchasing.suggestion.read',
      label: 'Bestellvorschläge lesen',
      group: GROUP,
      description: 'Was am Standort unter Meldebestand liegt, was dazu bestellt oder unterwegs ist und was das System vorschlägt.',
    }),
    definePermission({
      key: 'purchasing.suggestion.create',
      label: 'Bestellvorschläge bearbeiten und Anforderungen entscheiden',
      group: GROUP,
      description: 'Vorschläge festhalten und Anforderungen der Filialen ablehnen. Bestellungen daraus anlegen verlangt zusätzlich „Bestellungen bearbeiten“.',
    }),
    definePermission({
      key: 'purchasing.requisition.create',
      label: 'Ware beim Einkauf anfordern',
      group: GROUP,
      description: 'Bedarf des eigenen Standorts melden – der Einkauf sieht die Anforderung in seinem Bestellvorschlag.',
    }),
    definePermission({
      key: 'purchasing.order.read',
      label: 'Bestellungen lesen',
      group: GROUP,
      description: 'Bestellungen des Standorts mit bestellten, gelieferten und offenen Mengen. Preise nur mit „Einstandspreise sehen“.',
    }),
    definePermission({
      key: 'purchasing.order.write',
      label: 'Bestellungen anlegen, ändern und als bestellt markieren',
      group: GROUP,
      description: 'Über der eigenen Freigabegrenze wartet die Bestellung auf die Freigabe einer zweiten Person.',
    }),
    definePermission({
      key: 'purchasing.order.send',
      label: 'Bestellungen per E-Mail an Lieferanten senden',
      group: GROUP,
      description: 'Nur bestellte Bestellungen. Die E-Mail enthält die Einkaufspreise; ihren Inhalt im Postausgang sieht nur, wer „Einstandspreise sehen“ hält.',
    }),
    definePermission({
      key: 'purchasing.order.approve',
      label: 'Bestellungen freigeben',
      group: GROUP,
      description: 'Bis zur Grenze ohne weitere Freigabe bestellen und Bestellungen anderer bis zu dieser Grenze freigeben – nie die eigene.',
      limits: [{ key: 'maxOrderValueCents', label: 'Höchster Warenwert je Bestellung', unit: 'cents' }],
    }),
    definePermission({
      key: 'purchasing.invoice.read',
      label: 'Eingangsrechnungen lesen',
      group: GROUP,
    }),
    definePermission({
      key: 'purchasing.invoice.check',
      label: 'Eingangsrechnungen erfassen, prüfen und freigeben',
      group: GROUP,
      description: 'Abgleich mit Bestellung und Wareneingang; Abweichungen annehmen oder Gutschrift anfordern; Freigabe zur Zahlung.',
    }),
    definePermission({
      key: 'purchasing.settings.write',
      label: 'Toleranzen des Einkaufs einstellen',
      group: GROUP,
      description: 'Erlaubte Überlieferung und Toleranzen der Rechnungsprüfung.',
      locationScoped: false,
    }),
  ],
})
